import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RefundDetailsModal = ({ isOpen, refund, onClose }) => {
  if (!isOpen || !refund) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-4xl bg-card rounded-xl elevation-2xl overflow-hidden border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="FileText" size={24} className="text-primary" />
              Refund Request Details
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-smooth"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          {/* Request Overview */}
          <div className="bg-background border border-border rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Request ID</p>
                <p className="text-base font-mono font-semibold text-foreground">{refund?.requestId}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Transaction ID</p>
                <p className="text-base font-mono font-semibold text-foreground">{refund?.transactionId}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Customer Name</p>
                <p className="text-base font-semibold text-foreground">{refund?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Customer Email</p>
                <p className="text-base text-foreground">{refund?.customerEmail}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Original Amount</p>
                <p className="text-base font-semibold text-foreground">₹{refund?.originalAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Refund Amount</p>
                <p className="text-base font-semibold text-success">₹{refund?.refundAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Request Date</p>
                <p className="text-base text-foreground">{new Date(refund.requestDate)?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Approval Level</p>
                <p className="text-base text-foreground capitalize">{refund?.approvalLevel?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>

          {/* Reason & Documentation */}
          <div className="bg-background border border-border rounded-lg p-6 mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="MessageSquare" size={18} className="text-primary" />
              Reason & Documentation
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Reason Code</p>
                <p className="text-sm text-foreground capitalize">{refund?.reasonCode?.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Detailed Reason</p>
                <p className="text-sm text-foreground">{refund?.reason}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Dispute Documentation</p>
                <p className="text-sm text-foreground">{refund?.disputeDocumentation}</p>
              </div>
            </div>
          </div>

          {/* Attachments */}
          {refund?.attachments?.length > 0 && (
            <div className="bg-background border border-border rounded-lg p-6 mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Paperclip" size={18} className="text-primary" />
                Attachments ({refund?.attachments?.length})
              </h3>
              <div className="space-y-2">
                {refund?.attachments?.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
                    <div className="flex items-center gap-3">
                      <Icon name="File" size={18} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{attachment}</span>
                    </div>
                    <button className="p-2 hover:bg-muted rounded-md transition-smooth">
                      <Icon name="Download" size={16} className="text-primary" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Communication History */}
          <div className="bg-background border border-border rounded-lg p-6 mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="History" size={18} className="text-primary" />
              Communication History
            </h3>
            <div className="space-y-3">
              {refund?.communicationHistory?.map((comm, index) => (
                <div key={index} className="flex gap-3 pb-3 border-b border-border last:border-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="MessageCircle" size={14} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{comm?.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(comm.date)?.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="lg"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundDetailsModal;